import React, { useEffect, useCallback, useState } from 'react';

import { kebabCase } from 'lodash';
import { GripVertical, TrashIcon } from 'lucide-react';
import { Badge, ButtonIcon, cn, DefaultTooltip, ListItem, Separator } from 'twyne-new.ui';

import FieldProperties from '../properties';

import Collapsible from './Collapsible';
import { SortableDropzone, SortableComponent } from './utils'

export default class FormBuilder extends React.Component {
  state = {
    steps: [],
  }

  debug = true

  componentWillMount() {
    this.setState({ steps: this.props.data })
  }

  addFieldData = template => {
    const col = {}
    col.field = template.value
    col.label = template.componentLabel || template.label
    col.class = kebabCase(template.label)

    col.custom_field = template.value.indexOf("cf_") === 0
    col.field_type = template.field_type
    col.field_options = template.field_options
    return col
  }

  handleDelete = (data, onDeleteItSelf) => idx => {
    const removedItem = data.splice(idx, 1).pop()
    if (onDeleteItSelf && data.length == 0) {
      onDeleteItSelf()
    }
    this.setState({})
    return removedItem
  }

  handleAddCol = cols => evt => {
    const { type } = evt.item.dataset
    const { newIndex } = evt

    const totalSize = cols.reduce((size, col) => size + (col.size || 1), 0)

    if (totalSize >= 11) {
      cols.forEach(col => {
        col.size = undefined
      })
    }

    if (type === "palette") {
      const { data } = evt
      const col = this.addFieldData(data)
      cols.splice(newIndex, 0, col)
      this.setState({})
      return
    }

    const movedData = evt.moved
    movedData.size = undefined
    cols.splice(newIndex, 0, movedData)
    this.setState({})
  }
  handleAddRow = rows => evt => {
    const { newIndex, item: { dataset: { type } } } = evt

    if (type === "col") {
      const movedData = evt.moved
      rows.splice(newIndex, 0, { cols: [movedData] })
      this.setState({})
      return
    }

    if (type == "row") {
      const movedData = evt.moved
      rows.splice(newIndex, 0, movedData)
      this.setState({})
      return
    }

    const row = { cols: [] }
    rows.splice(newIndex, 0, row)
    this.handleAddCol(row.cols).call(this, evt)
  }

  handleAddStep = steps => evt => {
    const { newIndex, item: { dataset: { type } } } = evt

    if (type === "row") {
      return
    }

    const step = []
    steps.splice(newIndex, 0, step)
    this.handleAddRow(step).call(this, evt)
  }

  RootContainer = () => {
    const { steps } = this.state
    const handleDeleteStep = this.handleDelete(steps)

    return <SortableComponent onChange={() => this.setState({})}
      className="flex flex-col gap-4"
      options={{ group: { name: "root", put: ["palette", "root", "step"] }, handle: ".move-step" }}
      data={steps}
      onDelete={handleDeleteStep}
      onAdd={this.handleAddStep}
    >
      {steps.map((step, idx) => <this.StepContainer key={idx}
        idx={idx}
        rows={step}
        onDeleteItSelf={() => handleDeleteStep(idx)} />,
      )}
      <SortableDropzone render={!steps?.length} />
    </SortableComponent>
  }

  StepHeader = ({ title, onDeleteItSelf }) => <>
    <div className='px-1 py-2 w-full flex justify-between items-center'>
      {title}
      <ButtonIcon
        content='Delete step'
        onClick={evt => {
          evt.stopPropagation()
          onDeleteItSelf(evt)
        }}
      >
        <TrashIcon size='14' />
      </ButtonIcon>
    </div>
  </>

  StepContainer = ({ rows, onDeleteItSelf, idx }) => {
    const handleDeleteRow = this.handleDelete(rows, onDeleteItSelf)
    const hoveredId = `hover-${idx}`
    const title = `Step ${idx + 1}`

    return <Collapsible className='move-step' data-type="step" header={<this.StepHeader title={title} onDeleteItSelf={onDeleteItSelf} />}>
      <SortableComponent onChange={() => this.setState({})}
        className={cn(`
          flex flex-col 
          [&>.sortable-chosen]:mr-4 [&>.sortable-chosen]:ml-10!
          [&>.sortable-ghost]:mr-4 [&>.sortable-ghost]:ml-10!
          [&>.sortable-chosen:has(.move-row)]:mr-0! [&>.sortable-chosen:has(.move-row)]:ml-0!
          [&>.sortable-ghost:has(.move-row)]:mr-0! [&>.sortable-ghost:has(.move-row)]:ml-0!
          [&>.sortable-chosen]:rounded-xs [&>.sortable-ghost]:rounded-xs 
          [&>.sortable-ghost]:basis-0 [&>.sortable-ghost]:grow [&>.sortable-ghost]:max-w-full [&>.sortable-ghost]:min-w-0
          [&>.sortable-chosen]:basis-0 [&>.sortable-chosen]:grow [&>.sortable-chosen]:max-w-full [&>.sortable-chosen]:min-w-0
          [&>.sortable-chosen]:justify-center [&>.sortable-ghost]:justify-center
        `)}
        options={{ group: { name: "step", put: ["palette", "row", "step"] }, handle: ".move-row" }}
        data={rows}
        onDelete={handleDeleteRow}
        onAdd={this.handleAddRow}
      >
        {rows.filter(row => row && Array.isArray(row.cols)).map((row, idx) => <this.RowContainer key={idx}
          idx={idx}
          stepHoverId={hoveredId}
          cols={row.cols}
          onDeleteItSelf={() => handleDeleteRow(idx)} />,
        )}
        <div className='pt-6' />
      </SortableComponent>
    </Collapsible>
  }

  RowContainer = ({ cols = [], onDeleteItSelf, idx, stepHoverId }) => {
    const handleDeleteCol = this.handleDelete(cols, onDeleteItSelf)
    const hoveredId = `${stepHoverId}-${idx}`
    const put = useCallback((self, source) => {
      if (!Array.isArray(cols)) {
        return false
      }

      if (cols.length >= 6) {
        return false
      }

      const totalSize = cols.reduce((size, col) => size + (col.size || 2), 0)

      if (12 - totalSize < 2) {
        return false
      }

      return ["palette", "row"].includes(source.options.group.name)
    }, [cols.length])

    return <>
      <div className='group flex items-center p-2 px-4!' data-type="row">
        <div className='move-row cursor-move hover:twy-text-base-accent mr-2'>
          <GripVertical size='16' />
        </div>

        <SortableComponent onChange={() => this.setState({})}
          className={cn(`
            flex-1 min-w-0 flex -mx-1 
            [&>.sortable-chosen]:rounded-xs [&>.sortable-ghost]:rounded-xs 
            [&>.sortable-ghost]:basis-0 [&>.sortable-ghost]:grow [&>.sortable-ghost]:max-w-full [&>.sortable-ghost]:min-w-0
            [&>.sortable-chosen]:basis-0 [&>.sortable-chosen]:grow [&>.sortable-chosen]:max-w-full [&>.sortable-chosen]:min-w-0
            [&>.sortable-chosen]:justify-center [&>.sortable-ghost]:justify-center
          `)}
          options={{
            group: { name: "row", put },
            direction: "horizontal",
            handle: ".move-col",
          }}
          data={cols}
          onDelete={handleDeleteCol}
          onAdd={this.handleAddCol}
        >
          {cols.map((col, idx) => <this.FieldCol key={col.field}
            idx={idx}
            rowHoverId={hoveredId}
            data={col}
            cols={cols}
            onDeleteItSelf={() => handleDeleteCol(idx)} />,
          )}
        </SortableComponent>
      </div>
    </>
  }

  FieldCol = ({ data, onDeleteItSelf, cols, idx, rowHoverId }) => {
    const [collapseTips, setCollapseTips] = useState(false)
    useEffect(() => this.props.onChangeField(data.field, true), [])
    useEffect(() => () => this.props.onChangeField(data.field, false), []);

    const hoveredId = `${rowHoverId}-${idx}`
    const size = data?.size ? (data.size / 12) * 100 : undefined
    const title = `[${data.field}] ${data.label || ''}`
    const sizeStyle = size ? { flexBasis: `${size}%`, maxWidth: `${size}%`, width: `${size}%` } : undefined
    const sizeClass = size ? 'grow-0 shrink-0 min-w-0 box-border' : 'basis-0 grow max-w-full min-w-0 box-border'

    const valueTips = JSON.stringify(data)
    useEffect(() => {
      const container = document.getElementById(hoveredId)?.clientWidth
      const fullTipsWidth = document.getElementById(`${hoveredId}-tips-full`)?.clientWidth
      setCollapseTips(fullTipsWidth > (container / 2))
    }, [hoveredId, valueTips, cols.length])

    return <>
      <div className={`${sizeClass} px-1`} style={sizeStyle} id={hoveredId}>
        <FieldProperties
          size={size}
          onConflict={this.props.onConflict}
          customFields={this.props.customFields}
          selectedFields={this.props.selectedFields}
          target={hoveredId}
          data={data}
          cols={cols}
          onChange={() => this.setState({})}
        >
          <div className='move-col'>
            <ListItem className='border twy-border-base-accent rounded-xs p-2 w-full min-w-0 flex justify-between items-center cursor-move gap-1' >
              <DefaultTooltip content={title} triggerProps={{ className: 'block min-w-0 flex-1 truncate' }}>
                <span className='block min-w-0 flex-1 truncate'>{title}</span>
              </DefaultTooltip>
              <div className='flex gap-1 items-center relative shrink-0'>
                <this.FieldTips collapseTips={collapseTips} hoveredId={hoveredId} data={data} />
                <Separator orientation='vertical' className='py-2 m-0 mr-5!' />
                <ButtonIcon
                  content='Delete field'
                  className='absolute right-0 top-1/2 -translate-y-1/2 no-drag'
                  onClick={evt => {
                    evt.preventDefault()
                    evt.stopPropagation()
                    onDeleteItSelf(evt)
                  }}
                >
                  <TrashIcon size='14' />
                </ButtonIcon>
              </div>
            </ListItem>
          </div>
        </FieldProperties>
      </div>
    </>
  }

  FieldTips = ({ collapseTips, hoveredId, data: { field_required, autohide, controls: { populate, visibility } = {} } }) => {
    populate = populate && populate.actions && populate.actions.length || false
    visibility = visibility && visibility.actions.length || false
    const tips = [field_required && 'Required', autohide && 'Autohide', populate && 'Populate', visibility && 'Visibility'].filter(Boolean)
    const shouldCollapse = collapseTips && tips.length > 1
    const [collapseToCountOnly, setCollapseToCountOnly] = useState(false)

    useEffect(() => {
      const container = document.getElementById(hoveredId)?.clientWidth
      const collapsedWidth = document.getElementById(`${hoveredId}-tips-collapsed`)?.clientWidth
      setCollapseToCountOnly(!!collapsedWidth && collapsedWidth > (container / 2))
    }, [hoveredId, tips.length])

    return <div className='flex items-center gap-1' id={`${hoveredId}-tips`}>
      <div className='absolute invisible pointer-events-none h-0 overflow-hidden whitespace-nowrap' id={`${hoveredId}-tips-full`}>
        {tips.map((tip, idx) => <span key={idx} className='inline-block mr-1'>{tip}</span>)}
      </div>
      <div className='absolute invisible pointer-events-none h-0 overflow-hidden whitespace-nowrap' id={`${hoveredId}-tips-collapsed`}>
        {!!tips.length && <span className='inline-flex gap-1 items-center'>
          <Badge color='accent' variant='light' size='sm'>{tips[0]}</Badge>
          <Badge color='accent' variant='light' size='sm'>+{tips.length - 1}</Badge>
        </span>}
      </div>
      {shouldCollapse ? <this.FieldTipsCollapse tips={tips} collapseToCountOnly={collapseToCountOnly} /> : <this.FieldTipsExpand tips={tips} />}
    </div>
  }

  FieldTipsExpand = ({ tips }) => <>
    {tips.map(tip => <this.FieldTip key={tip} active tip={tipMessage[tip]}>{tip}</this.FieldTip>)}
  </>

  FieldTipsCollapse = ({ tips, collapseToCountOnly }) => <>
    {collapseToCountOnly ? <DefaultTooltip content={<>{tips.map((tip, idx) => <div key={idx} className='mb-2 last:mb-0'>{tip}</div>)}</>}>
      <Badge color='accent' variant='light' size='sm'>+{tips.length}</Badge>
    </DefaultTooltip> : <>
      <this.FieldTip active tip={tipMessage[tips[0]]}>{tips[0]}</this.FieldTip>
      <DefaultTooltip content={<>{tips.slice(1).map((tip, idx) => <div key={idx} className='mb-2 last:mb-0'>{tip}</div>)}</>}>
        <Badge color='accent' variant='light' size='sm'>+{tips.length - 1}</Badge>
      </DefaultTooltip>
    </>}
  </>

  FieldTip = ({ active, tip, children }) => {
    if (!active) {
      return null
    }

    return <>
      <DefaultTooltip content={tip} >
        <Badge color='accent' variant='light' size='sm' className='relative'>{children}</Badge>
      </DefaultTooltip>
    </>
  }

  render() {
    return <>
      {this.props.children}
      <this.RootContainer />
    </>
  }
}

const tipMessage = {
  Required: 'The consumer can not submit the form without this field.',
  Autohide: 'This field will be shown only when the consumer data is empty.',
  Populate: 'This field have rules for pre-populate or options.',
  Visibility: 'This field have rules for show or hide.',
}
